import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import MultiStepForm from '../components/MultiStepForm.jsx'
import WizardAside from '../components/WizardAside.jsx'
import { addManualLead } from '../lib/manualLeads.js'
import { logEvent } from '../lib/events.js'
import { useAuth } from '../auth.jsx'

// Add a lead by hand — the same split-screen wizard as New Segment / New
// Campaign. Manually-entered leads merge into the directory next to the
// imported data, stamped with who added them (the Source column + filter).
import { CATEGORIES, DEFAULT_CATEGORY, categoryPath } from '../lib/categories.js'

// One option per vertical in lib/categories.js.
const CATEGORY_OPTIONS = CATEGORIES.map((c) => ({ value: c.key, label: c.label }))

const US_STATES = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'],
  ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'], ['DC', 'Washington DC'], ['FL', 'Florida'],
  ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'],
  ['IA', 'Iowa'], ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'],
  ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'],
  ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'], ['NH', 'New Hampshire'],
  ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'],
  ['OH', 'Ohio'], ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'],
  ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'],
  ['WY', 'Wyoming'],
]

// Common industry tags for the auto-suggest (the directory's own `trade`
// vocabulary — the column keeps its schema name). Free text still allowed;
// suggestions just speed it up. Swap this list for your own when you load
// your data.
const TRADE_SUGGESTIONS = [
  'Masonry', 'Brick', 'Stone', 'Concrete', 'Block / CMU', 'Stucco & Plaster',
  'Tuckpointing & Restoration', 'Hardscape & Pavers', 'Retaining Walls', 'Chimney & Fireplace',
  'Foundation Repair', 'Waterproofing', 'Precast', 'Glass Block', 'Veneer',
  'Commercial Construction', 'Residential Construction', 'General Contracting',
]

export default function NewLead() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const requested = searchParams.get('category')
  const initialData = {
    category: CATEGORY_OPTIONS.some((c) => c.value === requested) ? requested : DEFAULT_CATEGORY,
  }

  const steps = [
    {
      id: 'company',
      canContinue: (d) =>
        (d.name || '').trim() ? true : 'Enter the company name to continue.',
      title: 'Who are you adding?',
      description: 'The company, where they are, and what they do.',
      render: (d, setField) => (
        <div className="mt-[26px] flex flex-col gap-y-5">
          <div className="flex flex-col gap-y-2">
            <span className="text-[13px] font-medium text-ink">Lead type</span>
            <div className="flex flex-wrap gap-2">
              {CATEGORY_OPTIONS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  className={`rounded-[10px] border px-4 py-2.5 text-[14px] font-semibold transition-colors ${
                    d.category === c.value
                      ? 'border-primary bg-primary-tint text-primary-dark'
                      : 'border-line bg-surface text-dim hover:bg-panel'
                  }`}
                  onClick={() => setField('category', c.value)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <Field label="Company name *" value={d.name} onChange={(v) => setField('name', v)} placeholder="Summit Analytics Inc." autoFocus />
          <div className="grid grid-cols-2 gap-x-4 max-[560px]:grid-cols-1">
            <Field label="City" value={d.city} onChange={(v) => setField('city', v)} placeholder="Denver" />
            <SuggestField
              label="State"
              value={d.state}
              onChange={(v) => setField('state', v)}
              onPick={(name) => setField('state', name)}
              getSuggestions={(text) => {
                const q = text.trim().toLowerCase()
                return US_STATES
                  .filter(([code, name]) => !q || name.toLowerCase().includes(q) || code.toLowerCase() === q)
                  .map(([, name]) => name)
                  .slice(0, 8)
              }}
              // Strict: typing only filters. On blur the text either snaps to a
              // real state (exact name/code, else the single remaining match)
              // or clears — "Alabamaddd" can never be stored.
              onCommit={(text) => {
                const q = text.trim().toLowerCase()
                if (!q) return setField('state', '')
                const exact = US_STATES.find(
                  ([code, name]) => name.toLowerCase() === q || code.toLowerCase() === q,
                )
                if (exact) return setField('state', exact[1])
                const matches = US_STATES.filter(([, name]) => name.toLowerCase().includes(q))
                setField('state', matches.length === 1 ? matches[0][1] : '')
              }}
              placeholder="Type to search — e.g. New…"
            />
          </div>
          <TokenField
            label="Industry"
            values={Array.isArray(d.trade) ? d.trade : []}
            onChange={(next) => setField('trade', next)}
            suggestions={TRADE_SUGGESTIONS}
            placeholder="Type to search trades…"
          />
        </div>
      ),
    },
    {
      id: 'contact',
      title: 'How do you reach them?',
      description: 'Contact details — all optional, add what you have.',
      render: (d, setField) => {
        const extras = d.extraContacts || []
        const patchExtra = (i, patch) =>
          setField(
            'extraContacts',
            extras.map((c, j) => (j === i ? { ...c, ...patch } : c)),
          )
        return (
          <div className="mt-[26px] flex flex-col gap-y-5">
            <div className="grid grid-cols-2 gap-x-4 max-[560px]:grid-cols-1">
              <Field label="Primary contact" value={d.contact_name} onChange={(v) => setField('contact_name', v)} placeholder="Jane Miller" />
              <Field label="Contact phone" value={d.contact_phone} onChange={(v) => setField('contact_phone', v)} placeholder="(303) 555-0142" />
            </div>
            <Field label="Email" value={d.email} onChange={(v) => setField('email', v)} placeholder="jane@summitanalytics.com" type="email" />
            <Field label="Website" value={d.website} onChange={(v) => setField('website', v)} placeholder="https://summitanalytics.com" />

            {extras.map((c, i) => (
              <div key={i} className="relative rounded-[12px] border border-line bg-panel p-4">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-[12px] font-semibold uppercase tracking-[0.03em] text-faint">
                    Additional contact {i + 1}
                  </span>
                  <button
                    type="button"
                    className="grid h-7 w-7 place-items-center rounded-[7px] text-faint transition-colors hover:bg-surface hover:text-danger"
                    onClick={() => setField('extraContacts', extras.filter((_, j) => j !== i))}
                    aria-label={`Remove additional contact ${i + 1}`}
                  >
                    ×
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-x-3 max-[640px]:grid-cols-1 max-[640px]:gap-y-3">
                  <Field label="Name" value={c.name} onChange={(v) => patchExtra(i, { name: v })} placeholder="Chris Doe" />
                  <Field label="Role" value={c.role} onChange={(v) => patchExtra(i, { role: v })} placeholder="Estimator" />
                  <Field label="Phone" value={c.phone} onChange={(v) => patchExtra(i, { phone: v })} placeholder="(303) 555-0170" />
                </div>
              </div>
            ))}
            <button
              type="button"
              className="inline-flex items-center gap-x-2 self-start rounded-[10px] border border-dashed border-line bg-surface px-4 py-[9px] text-[13px] font-semibold text-primary-dark transition-colors hover:border-primary hover:bg-primary-tint"
              onClick={() => setField('extraContacts', [...extras, { name: '', role: '', phone: '' }])}
            >
              + Add another contact
            </button>
          </div>
        )
      },
    },
    {
      id: 'firmographics',
      title: 'Company details',
      description: 'Optional firmographics — they power filters and scoring.',
      render: (d, setField) => (
        <div className="mt-[26px] flex flex-col gap-y-5">
          <div className="grid grid-cols-2 gap-x-4 max-[560px]:grid-cols-1">
            <Field label="Established (year)" value={d.established} onChange={(v) => setField('established', v)} placeholder="2008" />
            <Field label="Company size" value={d.company_size} onChange={(v) => setField('company_size', v)} placeholder="10-49 employees" />
          </div>
          <div className="grid grid-cols-2 gap-x-4 max-[560px]:grid-cols-1">
            <Field label="Annual volume" value={d.annual_volume} onChange={(v) => setField('annual_volume', v)} placeholder="$1M - $5M" />
            <Field label="Service area" value={d.service_area} onChange={(v) => setField('service_area', v)} placeholder="Denver metro; Boulder" />
          </div>
        </div>
      ),
    },
  ]

  function handleComplete(d) {
    const name = (d.name || '').trim()
    // Refuse with a reason — a bare return would look like a dead button.
    if (!name) return 'Enter the company name before saving.'
    // Store the canonical "City, ST" shape the imported rows use.
    const stateEntry = US_STATES.find(
      ([code, label]) =>
        label.toLowerCase() === (d.state || '').trim().toLowerCase() ||
        code.toLowerCase() === (d.state || '').trim().toLowerCase(),
    )
    // Only a real state ever reaches storage (the field snaps/clears on blur,
    // and this drops anything else as a belt-and-suspenders guard).
    const cityState = [(d.city || '').trim(), stateEntry ? stateEntry[0] : '']
      .filter(Boolean)
      .join(', ')
    addManualLead(
      d.category,
      {
        name,
        city_state: cityState,
        // Chips → the same ';'-packed shape the imported rows use.
        trade: Array.isArray(d.trade) ? d.trade.join('; ') : String(d.trade || '').trim(),
        // Additional contacts → the packed "name=…; role=…; phone=…" groups the
        // detail page already parses. Values are stripped of the pack syntax.
        contacts: (d.extraContacts || [])
          .filter((c) => (c.name || '').trim())
          .map((c) => {
            const clean = (v) => String(v || '').replace(/[;=]/g, ' ').trim()
            return [
              `name=${clean(c.name)}`,
              clean(c.role) && `role=${clean(c.role)}`,
              clean(c.phone) && `phone=${clean(c.phone)}`,
            ]
              .filter(Boolean)
              .join('; ')
          })
          .join('; '),
        contact_name: (d.contact_name || '').trim(),
        contact_phone: (d.contact_phone || '').trim(),
        email: (d.email || '').trim(),
        website: (d.website || '').trim(),
        established: (d.established || '').trim(),
        company_size: (d.company_size || '').trim(),
        annual_volume: (d.annual_volume || '').trim(),
        service_area: (d.service_area || '').trim(),
      },
      user,
    )
    logEvent('lead_added', {
      actor: user?.email || '',
      actorName: user?.name || '',
      detail: `Added ${CATEGORY_OPTIONS.find((c) => c.value === d.category)?.label} lead "${name}"`,
    })
    navigate(categoryPath(d.category))
  }

  return (
    <MultiStepForm
      steps={steps}
      initialData={initialData}
      finalLabel="Add lead"
      onComplete={handleComplete}
      onExit={() => navigate(`/leads/${initialData.category}`)}
      backLabel="Back to leads"
      aside={
        <WizardAside>
          <div className="w-[560px] max-w-full">
            <div className="rounded-[16px] border border-line bg-surface p-6 shadow-card">
              <div className="text-[12px] uppercase tracking-[0.04em] text-faint">
                Manual leads
              </div>
              <ol className="m-0 mt-3 flex flex-col gap-y-3 pl-0">
                {[
                  ['Merged in', 'Your entry appears in the directory next to the imported data.'],
                  ['Attributed', 'The Source column shows who added it — filter by teammate.'],
                  ['First-class', 'Lists, campaigns, enrichment, and scoring all work on it.'],
                ].map(([t, s], i) => (
                  <li key={t} className="flex list-none gap-x-3">
                    <span className="grid h-[26px] w-[26px] flex-none place-items-center rounded-full bg-primary-tint text-[12px] font-bold text-primary-dark">
                      {i + 1}
                    </span>
                    <div>
                      <div className="text-[14px] font-semibold text-ink">{t}</div>
                      <div className="text-[12.5px] leading-[1.45] text-dim">{s}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </WizardAside>
      }
    />
  )
}

const INPUT_CLS =
  'w-full rounded-[10px] border border-line bg-panel px-[14px] py-[11px] text-[14px] font-normal text-ink placeholder:text-faint focus:border-primary focus:shadow-[0_0_0_3px_rgba(60,135,247,0.2)] focus:outline-none'

// Text input with a suggestion dropdown. `getSuggestions(text)` returns the
// matches for what's typed; `onPick` replaces the value when one is chosen.
function SuggestField({ label, value, onChange, onPick, getSuggestions, placeholder, hint, onCommit }) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  useEffect(() => {
    function onPointer(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    return () => document.removeEventListener('mousedown', onPointer)
  }, [])
  const suggestions = open ? getSuggestions(value || '') : []
  return (
    <div className="relative flex flex-col gap-y-1.5 text-[13px] font-medium text-ink" ref={rootRef}>
      {label}
      <input
        type="text"
        className={INPUT_CLS}
        value={value || ''}
        onChange={(e) => {
          onChange(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => onCommit?.(value || '')}
        placeholder={placeholder}
      />
      {hint && <span className="text-[12px] font-normal text-faint">{hint}</span>}
      {suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 max-h-[220px] overflow-y-auto rounded-[10px] border border-line bg-surface p-1 shadow-[0_12px_32px_rgba(0,0,0,0.25)]">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              className="flex w-full items-center rounded-[7px] px-2.5 py-2 text-left text-[13.5px] font-normal text-ink transition-colors hover:bg-panel"
              onMouseDown={(e) => {
                e.preventDefault() // keep focus; commit before blur
                onPick(s)
                setOpen(false)
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Token multi-select: chosen values render as ×-chips inside the field, typing
// filters the dropdown, clicking picks, Enter creates whatever's typed. No
// separator syntax for the user to learn.
function TokenField({ label, values = [], onChange, suggestions, placeholder }) {
  const [text, setText] = useState('')
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const inputRef = useRef(null)
  useEffect(() => {
    function onPointer(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    return () => document.removeEventListener('mousedown', onPointer)
  }, [])

  const frag = text.trim().toLowerCase()
  const opts = suggestions
    .filter(
      (s) =>
        !values.some((v) => v.toLowerCase() === s.toLowerCase()) &&
        (!frag || s.toLowerCase().includes(frag)),
    )
    .slice(0, 8)

  function add(raw) {
    const v = raw.trim()
    setText('')
    if (!v) return
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) return
    onChange([...values, v])
  }

  return (
    <div className="relative flex flex-col gap-y-1.5 text-[13px] font-medium text-ink" ref={rootRef}>
      {label}
      <div
        className="flex min-h-[46px] w-full cursor-text flex-wrap items-center gap-1.5 rounded-[10px] border border-line bg-panel px-3 py-2 transition-[border-color,box-shadow] focus-within:border-primary focus-within:shadow-[0_0_0_3px_rgba(60,135,247,0.2)]"
        onClick={() => {
          inputRef.current?.focus()
          setOpen(true)
        }}
      >
        {values.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-x-1 rounded-[7px] border border-line bg-surface px-2 py-[3px] text-[12.5px] font-medium text-ink"
          >
            <button
              type="button"
              className="text-faint transition-colors hover:text-danger"
              onClick={(e) => {
                e.stopPropagation()
                onChange(values.filter((x) => x !== v))
              }}
              aria-label={`Remove ${v}`}
            >
              ×
            </button>
            {v}
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className="min-w-[120px] flex-1 border-none bg-transparent p-0 text-[14px] font-normal text-ink outline-none placeholder:text-faint"
          value={text}
          onChange={(e) => {
            setText(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add(text)
            } else if (e.key === 'Backspace' && !text && values.length) {
              onChange(values.slice(0, -1))
            }
          }}
          placeholder={values.length ? '' : placeholder}
        />
      </div>
      {open && (opts.length > 0 || frag) && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 max-h-[220px] overflow-y-auto rounded-[10px] border border-line bg-surface p-1 shadow-[0_12px_32px_rgba(0,0,0,0.25)]">
          {opts.map((s) => (
            <button
              key={s}
              type="button"
              className="flex w-full items-center rounded-[7px] px-2.5 py-2 text-left text-[13.5px] font-normal text-ink transition-colors hover:bg-panel"
              onMouseDown={(e) => {
                e.preventDefault()
                add(s)
              }}
            >
              {s}
            </button>
          ))}
          {frag && !opts.some((s) => s.toLowerCase() === frag) && (
            <button
              type="button"
              className="flex w-full items-center gap-x-1.5 rounded-[7px] px-2.5 py-2 text-left text-[13.5px] font-normal text-primary-dark transition-colors hover:bg-panel"
              onMouseDown={(e) => {
                e.preventDefault()
                add(text)
              }}
            >
              Create “{text.trim()}” <span className="text-[11.5px] text-faint">↵</span>
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function Field({ label, value, onChange, placeholder, hint, type = 'text', autoFocus }) {
  return (
    <label className="flex flex-col gap-y-1.5 text-[13px] font-medium text-ink">
      {label}
      <input
        type={type}
        autoFocus={autoFocus}
        className="rounded-[10px] border border-line bg-panel px-[14px] py-[11px] text-[14px] font-normal text-ink placeholder:text-faint focus:border-primary focus:shadow-[0_0_0_3px_rgba(60,135,247,0.2)] focus:outline-none"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {hint && <span className="text-[12px] font-normal text-faint">{hint}</span>}
    </label>
  )
}
